// StoreLocationFormModal.jsx
import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import GeneralInfoFields from "./GeneralStoreInfoSection";
import CoordinateFields from "./CoordinateSection";
import CustomHoursFields from "./CustomHoursSection";
import { useSnackbarAlert } from "../../../hooks/useSnackbarAlert";
import api from "../../../services/api";

const defaultHours = { open: "", close: "" };
const defaultForm = {
  name: "",
  address: "",
  city: "",
  postalCode: "",
  province: "",
  country: "",
  phone: "",
  email: "",
  googleMapsUrl: "",
  coordinates: { lat: "", lng: "" },
  customHours: {
    mon: { ...defaultHours },
    tue: { ...defaultHours },
    wed: { ...defaultHours },
    thu: { ...defaultHours },
    fri: { ...defaultHours },
    sat: { ...defaultHours },
    sun: { ...defaultHours },
  },
};

const generateInternalName = (displayName) => {
  return displayName
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, ""); // Remove any non-alphanumeric/underscore
};

const StoreLocationFormModal = ({ open, onClose, onSuccess, editData }) => {
  const snackbar = useSnackbarAlert();
  const [form, setForm] = useState(defaultForm);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setForm(editData || defaultForm);
    setErrors({});
  }, [editData, open]);

  const validate = () => {
    const required = [
      "displayName",
      "name",
      "storeCode",
      "address",
      "city",
      "postalCode",
      "province",
      "country",
      "phone",
      "email",
      "googleMapsUrl",
    ];
    const newErrors = {};
    for (let key of required) {
      if (!form[key]?.trim()) newErrors[key] = `${key} is required`;
    }
    if (!form.coordinates.lat || !form.coordinates.lng)
      newErrors.coordinates = "Coordinates (lat/lng) are required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setForm((prev) => {
      const updated = { ...prev, [field]: value };

      // If editing displayName, also auto-update internal name
      if (field === "displayName") {
        updated.name = generateInternalName(value);
      }

      return updated;
    });

    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleCoordinateChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      coordinates: { ...prev.coordinates, [field]: value },
    }));
    setErrors((prev) => ({ ...prev, coordinates: undefined }));
  };

  const handleHoursChange = (day, type, value) => {
    setForm((prev) => ({
      ...prev,
      customHours: {
        ...prev.customHours,
        [day]: { ...prev.customHours[day], [type]: value },
      },
    }));
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      if (editData?._id) {
        await api.put(`/admin/stores/${editData._id}`, form);
        snackbar.success("Store updated successfully!");
      } else {
        await api.post("/admin/stores", form);
        snackbar.success("Store created successfully!");
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error saving store location:", error);
      snackbar.error("Failed to save. Please try again.");
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 700,
          height: "80vh",
          bgcolor: "background.paper",
          boxShadow: 24,
          borderRadius: 2,
          p: 3,
          overflow: "auto",
        }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}>
          <Typography variant="h6">
            {editData ? "Edit" : "Add"} Store Location
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Grid container spacing={2}>
          <GeneralInfoFields
            form={form}
            errors={errors}
            onChange={handleChange}
          />
          <CoordinateFields
            lat={form.coordinates.lat}
            lng={form.coordinates.lng}
            error={errors.coordinates}
            onChange={handleCoordinateChange}
          />
          <CustomHoursFields
            hours={form.customHours}
            onChange={handleHoursChange}
          />
        </Grid>

        <Button
          variant="contained"
          fullWidth
          sx={{ mt: 4 }}
          onClick={handleSubmit}>
          {editData ? "Update Store" : "Add Store"}
        </Button>
      </Box>
    </Modal>
  );
};

export default StoreLocationFormModal;
